import React from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../components/content-box/ContentBox";
import PageActions from "../../../../base/PageActions";
import '../../../../styles/Common.scss';
import '../WorldPages.scss';
import '../WorldDynamicContent.scss';
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";

interface ReligionPageProps {
}

const ReligionPage = (props: ReligionPageProps) => {

    const page: PageActions = new PageActions({
        title: `Casarok Religion - Chase the Fox`,
        description: "Religions in Casarok have mostly been dictated by the gods and goddesses, spawning different myths and faiths!",
        keywords: ["casarok", "religion", "pantheon", "gods and goddesses", "fantasy gods", "fantasy religion"],
        imgSrc: `chase/world/magic/classes/wiccan.png`,
        url: `world/religion`
    });

    return page.generateMetaContent(
        <>
            <ContentBox header="Religions in Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <p>
                        Religions in Casarok are divided between
                        their <NavLink className="world-link" to="/world/history">pre- and post-Cataclysmic Age</NavLink> formations.
                        Prior to the Great Cataclysm, religions were largely polytheistic while more modern religions
                        tend to be monotheistic in nature.
                    </p>
                    <h3 className="headline">Pre-Cataclysm Religion</h3>
                    <p>
                        The Old World religions of Casarok were largely polytheistic being centered around the pantheon
                        of gods and goddesses that directly influenced the world. Different regions of the single
                        continent world generally worshipped a handful of gods and goddesses among the pantheon. Shrines
                        and temples were built in their honor, managed by the priests and priestesses who were blessed
                        by the deities of those shrines. Each deity had a mortal champion that would directly enact
                        their will. All such mortals are granted divine magical powers, usually much more powerful than
                        can be learned through normal means. Such magic was the only way one could learn spells from
                        the <NavLink className="world-link" to="/world/magic/schools">School of the Divine</NavLink>.
                    </p>
                    <p>
                        Faiths differed for <NavLink className="world-link" to="/world/bestiary">Aspects and Draconics</NavLink>, however.
                        Aspects would usually only worship their patron or matron deity. For example, most kitsune would
                        only serve Mother. Some Aspects around the world would also serve gods and goddesses who closely
                        aligned with their parent god or goddess as a minor deity in their faith. Draconics, on the
                        other hand, worshipped several different deities based on those who blessed them during the
                        Draconic Age.
                    </p>
                    <p>
                        <ul className="spaced">
                            <li>
                                <NavLink className="world-link" to="pantheon">Pantheon</NavLink>: There are a litany of
                                gods and goddesses in Casarok, each in carge of different powers and aspects of the world.
                            </li>
                            <li>
                                <NavLink className="world-link" to="mythology">Mythology</NavLink>: Various myths were
                                formed from the teachings of the priests and priestesses of Casarok.
                            </li>
                        </ul>
                    </p>
                    <h3 className="headline">Post-Cataclysm Religion</h3>
                    <p>
                        After the Cataclysm, the gods and goddesses of Casarok mostly left the mortals to their own
                        devices. As such the anthros of the world worshipped them less and less until not only were
                        the polytheistic religions of the Old World lost but the pantheon was almost entirely
                        forgotten. The modern people of Casarok don't even believe the "Old Gods" ever existed. They
                        believe the few myths that remain are purely fictional. It is similar to how ancient
                        mythologies like those of the Greeks and Norse are treated in modern Earth society.
                    </p>
                    <p>
                        Some of the teachings did persist through the Recovery Age and morphed into their own faiths.
                        The largest of these religions are as follows:
                        <ul className="spaced">
                            <li>
                                <strong>Madarism</strong>: The worship of the Great Mother and belief that life on
                                Casarok is sacred. Teachings of the various sects of Madarist faith focus on love and
                                understanding, the sanctity of the creation of new life, and protection of family and
                                the world itself. Madarism is the largest religion in the modern world, and churches and
                                temples are constructed all over Casarok to spread the teachings of the mysterious
                                Great Mother.
                            </li>
                            <li>
                                <strong>Endrianism</strong>: The belief that the Endtimes, the destruction of all life
                                on Casarok, is inevitable and that the only way to escape a gruesome demise is to align
                                with the dark will that will carry out the task. Few Endrianists know the identity of
                                the dark entity who will bring about the death of all life on the planet, except for
                                those who are corrupted by the whispers of Ragnarok. Generally, cults form and prey on
                                those of weak will to join them on this quest.
                            </li>
                            <li>
                                <strong>Astrianism</strong>: The worship of the Star Serpent who provides light to the
                                world. This faith mostly focuses on karma; doing deeds of kindness and generosity
                                pleases the Star Serpent and provides boons while nefarious deeds are punished. Early
                                Astrianists are the ones who determined the modern-day constellations in the night sky.
                                In the modern day, this faith takes on multiple forms. Coeloastrianists believe in a
                                white Star Serpent who brings the good to Heaven. Tarastrianists believe in a black Star
                                Serpent who sends the wicked to the Underworld. Tempastrianists believe in a golden Star
                                Serpent who will consider all future possibilities and make things right for them.
                                Finally, Orthodox Astrianists believe in a shimmering Star Serpent who guides them
                                via the constellations, similar to astrology.
                            </li>
                            <li>
                                <strong>Sylvianism</strong>: The belief of harmony with nature and the environment, set
                                into motion by Saint Sylvia during the Recovery Age. Saint Sylvia led several peoples of
                                Centralia to the dangerous forests of the Eldwood, tamed the monsters and floradons
                                lurking there, and established a commune to guide her people. These teachings are
                                similar to those of the goddess Domathair, the goddess of nature. Sylvianists believe in
                                the sanctity of nature and the close bonds that all anthros have with the ecosystem.
                                Thus, nature must be protected at all costs. While many sects are peaceful, some can be
                                quite aggressive in their defense of the environment.
                            </li>
                            <li>
                                <strong>Zadanismm</strong>: The belief in magical power and the life force that drives
                                it. There are 12 deities in this religion, one for each of
                                the <NavLink className="world-link" to="/world/magic/elements">magical elements</NavLink>. Zadanists
                                believe in the sanctity of magical power in Casarok and that one's destiny is truly
                                driven by their own actions. Some sects have splintered off in more modern times as
                                Mage-tech, the merging of magic and technology, was invented. While some Zadanists
                                believe that Mage-tech is an acceptable means as it is integrated into our daily lives,
                                more traditional sects reject it as a false means of using magic or even outright forbid
                                it.
                            </li>
                            <li>
                                <strong>Spiritualism</strong>: While not a religion in its own right, many Casarokans
                                are spiritual. Some may believe in a god or goddess who guides them through various
                                spiritual teachings. Others are atheist and only wish to connect with their inner
                                selves. Those of the deepest spiritual nature are guided by their ancestors or the
                                monks of a certain spiritualistic faith to the hidden location of Shangri'la, one of the
                                Seven Magical Wonders of the World, to join their ranks.
                            </li>
                        </ul>
                    </p>
                </div>
            </ContentBox>
        </>
    );
}

export default ReligionPage;